import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { lazy, Suspense } from 'preact/compat';
import type { JSX } from 'preact';
import { type Theme } from './components/brand';
import { readTheme, subscribeTheme } from './lib/theme';
import { VersionSelector } from './components/version-selector';
import { SnippetsMenu, HistoryMenu } from './components/editor-menus';
import { InputPanel } from './components/input-panel';
import { Toast } from './components/toast';
import { Icons } from './components/icons/icon-set';
import { ResultsPanel } from './features/results/results-panel';
import type { ResultMap } from './features/results/result-types';
import { toRunDisplay } from './features/run/run-result-display';
import { DEFAULT_OUTPUT_SNAPSHOT, DEFAULT_OUTPUT_SNAPSHOT_VERSION } from './data/default-output-snapshot';
import { useRunController } from './features/run/run-controller';
import { useRuntimeCache } from './features/settings/use-runtime-cache';
import { PHP_VERSIONS, DEFAULT_VERSION, orderVersions } from './data/php-versions';
import { SNIPPETS, DEFAULT_CODE, type Snippet } from './data/snippets';
import { LS } from './lib/persisted-state';
import { formatPhp } from './lib/format-php';
import { encodeShare, decodeShare, buildShareUrl, socialShareIntent, type ShareNetwork } from './share';
import { pushRun } from './features/history/history-store';
import type { HistoryEntry, Prefs } from './app-types';

// CodeMirror is the heaviest dependency in the shell. Lazy-load the editor so the
// app-shell initial JS stays under budget (~80 KB gz); the editor chunk fetches
// right after first paint. A skeleton fills the pane so there is no layout shift.
const CodeEditor = lazy(() =>
  import('./editor/code-editor').then((m) => ({ default: m.CodeEditor })),
);

// Modals are interaction-gated, so lazy-load them (JS + their co-located
// modals.css). This keeps the modal markup AND ~220 lines of modal CSS out of the
// render-blocking app shell; the chunk fetches on first open. Vite waits for the
// chunk's CSS before resolving the import, so there is no flash of unstyled modal.
const ShareModal = lazy(() =>
  import('./components/share-modal').then((m) => ({ default: m.ShareModal })),
);
const SettingsModal = lazy(() =>
  import('./components/settings-modal').then((m) => ({ default: m.SettingsModal })),
);
const WasmDownloadModal = lazy(() =>
  import('./features/run/wasm-download-modal').then((m) => ({ default: m.WasmDownloadModal })),
);

// Default preferences, merged over any persisted subset so older saved prefs
// (missing keys added after release, e.g. displayMode) get sane fallbacks.
const DEFAULT_PREFS: Prefs = { fontSize: 14, wrap: false, autoRun: true, displayMode: 'auto' };

// Real build-time output of DEFAULT_CODE (data/default-output-snapshot.ts), run
// once through the SAME transform a live result uses so the seeded output pane
// renders identically to a real run. Tool-first first paint: the visitor lands on
// output already on screen, with NO engine download (protects LCP). Constant +
// SSR-stable, so the pre-hydration render can emit it into static HTML and the
// client's first render matches byte-for-byte (no hydration mismatch).
const OUTPUT_SEED_MAP: ResultMap = {
  [DEFAULT_OUTPUT_SNAPSHOT_VERSION]: toRunDisplay(DEFAULT_OUTPUT_SNAPSHOT),
};

// The whole App is prerendered to static HTML (scripts/prerender.mjs + the dev
// middleware) and hydrated on the client. preact-render-to-string can't suspend
// and CodeMirror is a browser-only lazy chunk, so the editor renders as a skeleton
// during SSR — AND on the client's first (hydration) render — then swaps to the
// real editor in an effect. Matching the first client render to the SSR output is
// what keeps hydration clean (no mismatch, no orphaned skeleton node).
const EditorSkeleton = () => <div class="editor-wrap editor-loading" aria-busy="true" />;
const noop = () => {};

export function App() {
  // The App is a theme READER, not an owner: `data-theme` on <html> (set by the
  // inline THEME_INIT before first paint) is the source of truth. Init from it,
  // then subscribe below; the only writer is the vanilla toggle (THEME_TOGGLE).
  const [theme, setTheme] = useState<Theme>(() => readTheme());
  // Two-pass hydration gate: false during SSR and the first client render (so the
  // editor area matches the prerendered skeleton exactly), flipped true in an
  // effect to mount the lazy CodeMirror. See EditorSkeleton above.
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => setHydrated(true), []);
  const [code, setCode] = useState<string>(() => LS.get('code', DEFAULT_CODE));
  // Mirror the latest code so the async formatter can detect edits made during
  // its await and avoid clobbering them with a stale formatted snapshot.
  const codeRef = useRef(code);
  codeRef.current = code;
  // First-load default = SINGLE version (protects LCP / avoids 3×~20 MiB).
  const [versions, setVersions] = useState<string[]>(() => LS.get('versions', [DEFAULT_VERSION]));
  const [activeTab, setActiveTab] = useState(DEFAULT_VERSION);
  const { results, running, isRunning, run: runPhp, warm: warmRuntime, cancel } = useRunController();
  const [stdin, setStdin] = useState('');
  const [args, setArgs] = useState('');
  const [showInput, setShowInput] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [history, setHistory] = useState<HistoryEntry[]>(() => LS.get<HistoryEntry[]>('history', []));
  const [toast, setToast] = useState('');
  const [formatting, setFormatting] = useState(false);
  const [splitPct, setSplitPct] = useState(50);
  // Encoded payload for the Share dialog (computed on open). The code rides in
  // this token, not on a server.
  const [shareHash, setShareHash] = useState('');
  // Gates boot-time autorun until any shared-link hydration has settled.
  const [bootReady, setBootReady] = useState(false);
  // Runtime-cache state is the REAL source of which versions are downloaded.
  const { downloaded, cachedBytes, estimate, refresh, removeOne, clearAll } = useRuntimeCache();
  const [prefs, setPrefs] = useState<Prefs>(() => ({ ...DEFAULT_PREFS, ...LS.get<Partial<Prefs>>('prefs', {}) }));
  const setPref = <K extends keyof Prefs>(k: K, v: Prefs[K]) => setPrefs((p) => ({ ...p, [k]: v }));
  // Download-before-run queue: versions to fetch; `wasmThenRun` runs after caching.
  const [wasmQueue, setWasmQueue] = useState<string[] | null>(null);
  const [wasmThenRun, setWasmThenRun] = useState(true);

  /* theme is persisted by the vanilla writer THEME_TOGGLE, not here */
  useEffect(() => LS.set('code', code), [code]);
  useEffect(() => LS.set('versions', versions), [versions]);
  useEffect(() => LS.set('history', history), [history]);
  useEffect(() => LS.set('prefs', prefs), [prefs]);

  useEffect(() => {
    const r = document.documentElement.style;
    r.setProperty('--code-fz', prefs.fontSize + 'px');
    r.setProperty('--code-lh', Math.round(prefs.fontSize * 1.55) + 'px');
  }, [prefs.fontSize]);

  /* React to theme changes from the vanilla writer (data-theme is the source of
   * truth) so consumers like ShareModal's preview stay in sync. App never WRITES
   * the attribute — single writer = no race/desync. */
  useEffect(() => subscribeTheme(setTheme), []);

  const flash = (m: string) => {
    setToast(m);
    setTimeout(() => setToast(''), 1800);
  };

  /* Run the selected versions; `log=false` for boot-time autorun (avoids
   * spamming History on every page load). */
  const executeRun = useCallback(
    (log = true) => {
      const ordered = orderVersions(versions);
      void runPhp(code, ordered, { stdin, args });
      if (log) setHistory((prev) => pushRun(prev, { code, versions: ordered, stdin, args }));
    },
    [versions, code, stdin, args, runPhp],
  );
  const executeRunRef = useRef(executeRun);
  executeRunRef.current = executeRun;

  const run = useCallback(() => {
    if (!versions.length) {
      flash('Please select at least one PHP version');
      return;
    }
    const need = orderVersions(versions).filter((v) => !downloaded.includes(v));
    if (need.length) {
      setWasmThenRun(true);
      setWasmQueue(need);
      return;
    }
    executeRun();
  }, [versions, downloaded, executeRun]);

  const onWasmDone = useCallback(() => {
    setWasmQueue(null);
    void refresh();
    if (wasmThenRun) setTimeout(() => executeRunRef.current(), 40);
  }, [wasmThenRun, refresh]);

  /* Boot: hydrate from a shared-link hash (code rides in the URL, no server).
   * After hydrating, clear the hash so a later reload uses the user's working
   * copy (localStorage), not the shared snapshot. Marks boot ready either way. */
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    const token = location.hash.slice(1);
    if (!token) {
      setBootReady(true);
      return;
    }
    void decodeShare(token).then((p) => {
      if (p) {
        setCode(p.code);
        if (p.versions.length) setVersions(p.versions);
        setStdin(p.stdin || '');
        setArgs(p.args || '');
        if (p.stdin) setShowInput(true);
        // `history` here is the component's state array — use window.history.
        window.history.replaceState(null, '', location.pathname + location.search);
        flash('Loaded snippet from share link');
      }
      setBootReady(true);
    });
  }, []);

  /* Boot autorun (opt-in via Settings). Fires once, only when EVERY selected
   * version is already cached — so a first visit (uncached → would download
   * ~20 MiB) never auto-downloads, protecting LCP. */
  const autoRanRef = useRef(false);
  useEffect(() => {
    if (autoRanRef.current || !bootReady) return;
    if (!prefs.autoRun || !versions.length) return;
    if (!orderVersions(versions).every((v) => downloaded.includes(v))) return;
    autoRanRef.current = true;
    executeRun(false);
  }, [bootReady, prefs.autoRun, versions, downloaded, executeRun]);

  /* Idle-warm: after first paint settles, instantiate the first selected version's
   * worker (compiles its wasm) so the first Run feels instant. Gated to an
   * already-CACHED version — warming an uncached one would silently download
   * ~20 MiB and hurt LCP, which the first-load-single-version model exists to
   * avoid. Uncached first visits stay un-warmed and pay the one-time download via
   * the download-before-run modal. Fires once. */
  const warmedRef = useRef(false);
  useEffect(() => {
    if (warmedRef.current || !bootReady) return;
    const target = orderVersions(versions)[0];
    if (!target || !downloaded.includes(target)) return;
    warmedRef.current = true;
    const idle = (cb: () => void) =>
      typeof requestIdleCallback === 'function'
        ? requestIdleCallback(cb, { timeout: 2000 })
        : setTimeout(cb, 200);
    idle(() => warmRuntime(target));
  }, [bootReady, versions, downloaded, warmRuntime]);

  /* Cmd/Ctrl + Enter */
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        run();
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [run]);

  const toggleVersion = (v: string) => {
    setVersions((prev) => {
      const next = prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v];
      return next.length ? next : prev;
    });
  };

  const pickSnippet = (s: Snippet) => {
    setCode(s.code);
    if (s.stdin != null) {
      setStdin(s.stdin);
      setShowInput(true);
    }
    flash('Loaded example: ' + s.title);
  };

  const loadHistory = (h: HistoryEntry) => {
    setCode(h.code);
    setVersions(h.versions);
    setStdin(h.stdin || '');
    setArgs(h.args || '');
    flash('Session restored');
  };

  const format = async () => {
    if (formatting) return;
    setFormatting(true);
    const before = code;
    try {
      const { code: formatted, engine } = await formatPhp(before, activeTab);
      // If the user typed during the (possibly slow first-load) await, drop the
      // result rather than overwrite their newer edits with the stale snapshot.
      if (codeRef.current !== before) {
        flash('Format cancelled — code changed during formatting');
        return;
      }
      setCode(formatted);
      flash(engine === 'prettier' ? 'Code formatted' : 'Formatted (basic mode)');
    } finally {
      setFormatting(false);
    }
  };

  const reset = () => {
    setCode(DEFAULT_CODE);
    setStdin('');
    setArgs('');
    flash('Reset');
  };

  /* Single source so the modal, the warm-token effect, and quick-share buttons
   * all encode the exact same payload. */
  const sharePayload = useCallback(
    () => ({
      code,
      versions: orderVersions(versions),
      stdin: stdin || undefined,
      args: args || undefined,
    }),
    [code, versions, stdin, args],
  );

  /* Keep an encoded share token warm (debounced) so the pane-head quick-share
   * buttons can act synchronously inside the click gesture — clipboard writes
   * and popup `window.open` both need that gesture, which an await would spend.
   * Encoding is a cheap DEFLATE over the snippet; the debounce avoids running it
   * on every keystroke. */
  useEffect(() => {
    let cancelled = false;
    const id = setTimeout(() => {
      void encodeShare(sharePayload()).then((h) => {
        if (!cancelled) setShareHash(h);
      });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [sharePayload]);

  const onShare = async () => {
    const hash = await encodeShare(sharePayload());
    setShareHash(hash);
    setShowShare(true);
  };

  /* Quick-share from the Output header. When the warm token exists (the common
   * case), act synchronously so the gesture is preserved; otherwise fall back to
   * encoding first (rare: within the first debounce window after load). */
  const copyShareLink = () => {
    // Flash success only once the write actually resolves; a rejected/absent
    // clipboard falls back to pointing at the Share dialog instead of lying.
    const act = (hash: string) => {
      const url = buildShareUrl(hash, location.origin);
      const write = navigator.clipboard?.writeText(url);
      if (!write) {
        flash('Copy failed — use the Share dialog');
        return;
      }
      write.then(
        () => flash('Share link copied'),
        () => flash('Copy failed — use the Share dialog'),
      );
    };
    if (shareHash) act(shareHash);
    else void encodeShare(sharePayload()).then(act);
  };

  const quickShareTo = (network: ShareNetwork) => {
    const act = (hash: string) =>
      window.open(
        socialShareIntent(network, buildShareUrl(hash, location.origin)),
        '_blank',
        'noopener,noreferrer,width=600,height=520',
      );
    if (shareHash) act(shareHash);
    else void encodeShare(sharePayload()).then(act);
  };

  const downloadAll = () => {
    const miss = PHP_VERSIONS.filter((v) => !downloaded.includes(v.v)).map((v) => v.v);
    if (!miss.length) {
      flash('All runtimes already cached');
      return;
    }
    setWasmThenRun(false);
    setWasmQueue(miss);
  };

  /* splitter drag — `dragging` drives the active style; `dragRef` guards
   * the move handler against stale closures */
  const wsRef = useRef<HTMLElement>(null);
  const dragRef = useRef(false);
  const [dragging, setDragging] = useState(false);
  const startDrag = (e: JSX.TargetedMouseEvent<HTMLDivElement>) => {
    dragRef.current = true;
    setDragging(true);
    e.preventDefault();
    const move = (ev: MouseEvent) => {
      if (!dragRef.current || !wsRef.current) return;
      const r = wsRef.current.getBoundingClientRect();
      const pct = ((ev.clientX - r.left) / r.width) * 100;
      setSplitPct(Math.min(80, Math.max(20, pct)));
    };
    const up = () => {
      dragRef.current = false;
      setDragging(false);
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
  };

  const orderedVersions = orderVersions(versions);
  // Version list for chrome that paints on the FIRST render (the runbar's version
  // pills + the output count). `versions` comes from localStorage, so it can differ
  // from the SSR default — render the SSR-stable default until hydrated, then the
  // real selection, mirroring the editor/results two-pass gate above (keeps
  // hydration clean: no DOM mismatch on multi-version saved selections).
  const displayVersions = hydrated ? orderedVersions : [DEFAULT_VERSION];

  // Concise, screen-reader-only run status (announced via a dedicated aria-live
  // node, not the scrolling output region — see the results pane below).
  const srRunStatus = isRunning
    ? 'Running PHP…'
    : orderedVersions.length > 0 && orderedVersions.every((v) => results[v])
      ? 'Run finished'
      : '';

  // Show the real build-time snapshot in the output pane until the visitor edits
  // the default snippet or a run lands fresh output for the default version. Kept
  // narrow on purpose: only the untouched default code + default version selected,
  // and only when nothing real is running or already shown for it.
  const showOutputSeed =
    code === DEFAULT_CODE &&
    orderedVersions.includes(DEFAULT_OUTPUT_SNAPSHOT_VERSION) &&
    !results[DEFAULT_OUTPUT_SNAPSHOT_VERSION] &&
    !running[DEFAULT_OUTPUT_SNAPSHOT_VERSION];
  const displayResults = showOutputSeed ? { ...results, ...OUTPUT_SEED_MAP } : results;

  return (
    <div class="app" data-layout="vertical">
      <div class="playground-container">
        <div class="playground-card" id="playground" role="region" aria-label="PHP code editor and output">

      <div class="card-runbar">
        <VersionSelector all={PHP_VERSIONS} selected={displayVersions} onToggle={toggleVersion} downloaded={downloaded} />
        {/* One persistent button across idle/running — swap contents, not elements;
            CSS min-width holds the size constant. */}
        <button
          class="btn btn-primary btn-run"
          onClick={isRunning ? cancel : run}
          title={isRunning ? 'Stop running' : undefined}
        >
          {isRunning ? (
            <><span class="spin" aria-hidden="true" /> Stop</>
          ) : (
            <><Icons.play size={14} /> Run <span class="run-kbd hide-sm">⌘↵</span></>
          )}
        </button>
        <button class="btn" aria-label="Share" onClick={() => void onShare()}>
          <Icons.share size={16} /> <span class="hide-sm">Share</span>
        </button>
        <button
          class="btn btn-icon"
          aria-label="Settings"
          title="Settings"
          onClick={() => setShowSettings(true)}
        >
          <Icons.settings size={16} />
        </button>
      </div>
      <main class="workspace" ref={wsRef}>
        <section class="pane pane-editor" aria-label="Code editor" style={{ flexBasis: splitPct + '%', flexGrow: 0, flexShrink: 1 }}>
          <div class="editor-toolbar">
            <span class="dot" />
            <span class="filename">script.php</span>
            <div class="editor-toolbar-actions">
              <SnippetsMenu snippets={SNIPPETS} onPick={pickSnippet} />
              <HistoryMenu items={history} onPick={loadHistory} onClear={() => setHistory([])} />
              <span class="ph-sep" />
              <button
                class="iconbtn"
                onClick={format}
                disabled={formatting}
                title="Format code"
              >
                <Icons.wand size={15} />
              </button>
              <button
                class="iconbtn"
                data-on={showInput}
                onClick={() => setShowInput((s) => !s)}
                title="Input · STDIN / arguments"
              >
                <Icons.terminal size={15} />
              </button>
              <button
                class="iconbtn"
                onClick={() => {
                  navigator.clipboard?.writeText(code);
                  flash('Code copied');
                }}
                title="Copy code"
              >
                <Icons.copy size={15} />
              </button>
              <button class="iconbtn" onClick={reset} title="Reset">
                <Icons.reset size={15} />
              </button>
            </div>
          </div>
          {hydrated ? (
            <Suspense fallback={<EditorSkeleton />}>
              <CodeEditor value={code} onChange={setCode} wrap={prefs.wrap} />
            </Suspense>
          ) : (
            <EditorSkeleton />
          )}
          {showInput && (
            <InputPanel
              stdin={stdin}
              setStdin={setStdin}
              args={args}
              setArgs={setArgs}
              onClose={() => setShowInput(false)}
            />
          )}
        </section>

        <div class="splitter" data-drag={dragging} onMouseDown={startDrag} />

        <section
          class="pane pane-results"
          aria-label="Output"
          style={{ flexBasis: 100 - splitPct + '%', flexGrow: 0, flexShrink: 1 }}
        >
          {/* Dedicated polite status node — announces only the run lifecycle, NOT
              the (scroll-windowed) output itself. Putting aria-live on the whole
              results pane would make a screen reader re-announce rows on every
              virtualized scroll frame. */}
          <p class="sr-only" role="status" aria-live="polite">{srRunStatus}</p>
          <div class="pane-head">
            <Icons.terminal size={14} style={{ color: 'var(--primary-strong)' }} /> Output
            <span class="pane-head-run-note">
              {displayVersions.length > 1 ? (
                <>
                  <Icons.bolt size={12} /> {displayVersions.length} versions in parallel
                </>
              ) : (
                <>1 version</>
              )}
            </span>
            <span class="ph-sep" />
            <div class="pane-head-share">
              <button
                class="pane-share-btn"
                onClick={copyShareLink}
                title="Copy share link"
                aria-label="Copy share link"
              >
                <Icons.link size={14} /> <span class="hide-sm">Copy link</span>
              </button>
              <button
                class="iconbtn"
                onClick={() => quickShareTo('x')}
                title="Share to X"
                aria-label="Share to X"
              >
                <Icons.twitter size={15} />
              </button>
              <button
                class="iconbtn"
                onClick={() => quickShareTo('rd')}
                title="Share to Reddit"
                aria-label="Share to Reddit"
              >
                <Icons.reddit size={15} />
              </button>
            </div>
          </div>
          {hydrated ? (
            <ResultsPanel
              versions={orderedVersions}
              results={displayResults}
              running={running}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              displayMode={prefs.displayMode}
            />
          ) : (
            // SSR-stable placeholder: must match what the server emits (default
            // single version) — versions/displayMode come from localStorage and
            // diverge otherwise, corrupting the keyed columns/stacked DOM.
            // Seeded with the build-time output snapshot so the prerendered HTML
            // ships with real output in the pane before any JS runs.
            <ResultsPanel
              versions={[DEFAULT_VERSION]}
              results={OUTPUT_SEED_MAP}
              running={{}}
              activeTab={DEFAULT_VERSION}
              setActiveTab={noop}
              displayMode="auto"
            />
          )}
        </section>
      </main>
        </div>
      </div>

      <Suspense fallback={null}>
        {showShare && (
          <ShareModal onClose={() => setShowShare(false)} hash={shareHash} versions={orderedVersions} theme={theme} />
        )}
        {showSettings && (
          <SettingsModal
            onClose={() => setShowSettings(false)}
            all={PHP_VERSIONS}
            downloaded={downloaded}
            cachedBytes={cachedBytes}
            estimate={estimate}
            onClearCache={() => {
              void clearAll();
              flash('Runtime cache cleared');
            }}
            onClearOne={(v) => void removeOne(v)}
            onDownloadAll={downloadAll}
            prefs={prefs}
            setPref={setPref}
          />
        )}
        {wasmQueue && (
          <WasmDownloadModal queue={wasmQueue} all={PHP_VERSIONS} onComplete={onWasmDone} />
        )}
      </Suspense>
      <Toast msg={toast} />
    </div>
  );
}
